import { consoleInfoLog } from 'App/Helpers';
import UrlParams from 'App/Legacy/helpers/UrlParams';

class SharpDataStarService {
    /**
     * Initialize SharpDataStarService
     * @param sharpstarBaseUrl
     * @param source
     * @param campaignId
     * @param abortController
     */
    constructor(sharpstarBaseUrl, source, campaignId, abortController) {
        this.sharpstarBaseUrl = sharpstarBaseUrl;
        this.campaignId = campaignId;
        this.source = source;
        this.headers = {
            'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        };

        if (abortController) {
            this.signal = abortController.signal;
        }
        consoleInfoLog('## Init: SharpStar Service', 'init');
    }

    #fetchUrl(path) {
        return this.sharpstarBaseUrl + path;
    }

    /**
     * get data object and return it as URLSearchParams
     * @param dataObject
     */
    toUrlSearchParams(dataObject) {
        const data = new URLSearchParams();

        for (const [key, value] of Object.entries(dataObject)) {
            data.append(key, value);
        }

        return data;
    }

    /**
     * request getOffers from SharpStar
     * @param hash
     * @param isLead
     * @param rid
     * @param refid1
     * @param refid2
     * @param refid3
     * @param isBot
     * @param visitorId
     * @param customIsBot
     * @param botFailedChecks
     * @returns {Promise<Response>}
     */
    getOffers({
        hash,
        isLead,
        rid,
        refid1,
        refid2,
        refid3,
        isBot,
        customIsBot,
        visitorId,
        botFailedChecks,
    }) {
        const {
            rid: defaultRid,
            refid1: defaultRefid1,
            refid2: defaultRefid2,
            refid3: defaultRefid3,
        } = UrlParams;
        const fetchOptions = {
            method: 'GET',
            ...(this.signal && { signal: this.signal }),
        };
        const params = {
            is_lead: isLead,
            rid: rid || defaultRid,
            refid1: refid1 || defaultRefid1,
            refid2: refid2 || defaultRefid2,
            refid3: refid3 || defaultRefid3,
            source: this.source,
            campaign_id: this.campaignId,
            ...(hash && { hash }),
            ...(customIsBot && { cisbt: customIsBot }),
            ...(visitorId && { fngptid: visitorId }), // fngptid stands for fingerprint_id
            ...(typeof isBot === 'boolean' && { isbt: String(isBot) }), // isbt stands for is_bot
            ...(botFailedChecks && {
                btfldchk: botFailedChecks, // btfldchk stands for bot_failed_checks
            }),
        };

        return fetch(
            `${this.#fetchUrl('offer')}?${this.toUrlSearchParams(params)}`,
            fetchOptions
        );
    }

    /**
     * create lead with email
     * @param email
     * @param name
     * @param offerId
     * @param extraData
     * @param cpttknV3
     * @param hp_token
     * @param cpttknV2
     * @param hp_choice
     * @param hp_check
     * @returns {Promise<Response>}
     */
    createLead({
        email,
        name,
        offerId,
        extraData,
        cpttknV3,
        cpttknV2,
        hp_token,
        hp_choice,
        hp_check,
    }) {
        const { refid1, refid2, refid3, rid } = UrlParams;
        const fetchOptions = {
            method: 'POST',
            body: this.toUrlSearchParams({
                campaign_id: this.campaignId,
                source: this.source,
                offer_id: offerId,
                full_name: name,
                refid1,
                refid2,
                refid3,
                email,
                rid,
                hp_token,
                hp_choice,
                hp_check,
                ...(extraData && { extra_data: JSON.stringify(extraData) }),
                ...(cpttknV3 && { cpttknV3 }),
                ...(cpttknV2 && { cpttknV2 }),
            }),
            headers: this.headers,
            ...(this.signal && { signal: this.signal }),
        };

        return fetch(this.#fetchUrl('lead/create'), fetchOptions);
    }

    /**
     * create lead with push notifications
     * @param device_id
     * @param offerId
     * @param rid
     * @param refid1
     * @param refid2
     * @param refid3
     * @param extraData
     * @returns {Promise<Response>}
     */
    createPushLead({
        device_id,
        offerId,
        rid,
        refid1,
        refid2,
        refid3,
        extraData,
    }) {
        const {
            rid: defaultRid,
            refid1: defaultRefid1,
            refid2: defaultRefid2,
            refid3: defaultRefid3,
        } = UrlParams;
        const fetchOptions = {
            method: 'POST',
            body: this.toUrlSearchParams({
                offer_id: offerId,
                rid: rid || defaultRid,
                refid1: refid1 || defaultRefid1,
                refid2: refid2 || defaultRefid2,
                refid3: refid3 || defaultRefid3,
                campaign_id: this.campaignId,
                source: this.source,
                device_id,
                ...(extraData && { extra_data: JSON.stringify(extraData) }),
            }),
            headers: this.headers,
            ...(this.signal && { signal: this.signal }),
        };

        return fetch(this.#fetchUrl('create-push-lead'), fetchOptions);
    }

    /**
     * add device to push notifications to an exist lead
     * @param device_id
     * @param hash
     * @param offerId
     * @returns {Promise<Response>}
     */
    addPushDevice({ device_id, hash, offerId }) {
        const { refid1, refid2, refid3, rid } = UrlParams;
        const fetchOptions = {
            method: 'POST',
            body: this.toUrlSearchParams({
                campaign_id: this.campaignId,
                source: this.source,
                offer_id: offerId,
                device_id,
                refid1,
                refid2,
                refid3,
                hash,
                rid,
            }),
            headers: this.headers,
            ...(this.signal && { signal: this.signal }),
        };

        return fetch(this.#fetchUrl('add-push-device'), fetchOptions);
    }

    /**
     * add email registration to an exist lead
     * @param name
     * @param email
     * @param hash
     * @param offerId
     * @param extraData
     * @returns {Promise<Response>}
     */
    addContactInfo({ name, email, hash, offerId, extraData }) {
        const { refid1, refid2, refid3, rid } = UrlParams;
        const fetchOptions = {
            method: 'POST',
            body: this.toUrlSearchParams({
                campaign_id: this.campaignId,
                source: this.source,
                offer_id: offerId,
                full_name: name,
                refid1,
                refid2,
                refid3,
                email,
                hash,
                rid,
                ...(extraData && {
                    extra_data: JSON.stringify(extraData),
                }),
            }),
            headers: this.headers,
            ...(this.signal && { signal: this.signal }),
        };

        return fetch(this.#fetchUrl('add-contact-info'), fetchOptions);
    }

    /**
     * post device info and uuid ('refid3' value for dev or 'refid2_refid3' for prod) when user agreed for push notifications
     * @param browser - string (required)
     * @param device - string (required)
     * @param uuid - string (required) - refid3 or refid2_refid3 url parameters
     * @param extraData - json (optional)
     * @param isBot - boolean (optional)
     * @param campaignId - number (optional)
     * @param deviceId - string (optional)
     * @param hash - string (optional) - push lead hash
     * @param refid1 - string (optional)
     */
    addOneSignalLeadCountTest({
        browser,
        device,
        isBot,
        uuid,
        hash,
        refid1,
        campaignId,
        deviceId,
        extraData,
    }) {
        const fetchOptions = {
            method: 'POST',
            body: this.toUrlSearchParams({
                browser,
                device,
                uuid,
                source: this.source,
                ...(hash && { hash }),
                ...(refid1 && { refid1 }),
                ...(deviceId && { device_id: deviceId }),
                ...(campaignId && { campaign_id: campaignId }),
                ...(typeof isBot === 'boolean' ? { isbt: Number(isBot) } : {}),
                ...(extraData && {
                    additional_data: JSON.stringify(extraData),
                }),
            }),
            headers: this.headers,
            ...(this.signal && { signal: this.signal }),
        };

        return fetch(
            this.#fetchUrl('test/count-one-signal-request'),
            fetchOptions
        );
    }

    /**
     * post additional app data
     * @param hash - string (required)
     * @param campaignId - number (required)
     * @param extraData - object (required) - any number of key:value pairs
     */
    addExtraData({ hash, campaignId, extraData }) {
        const fetchOptions = {
            method: 'POST',
            body: this.toUrlSearchParams({
                hash,
                campaign_id: campaignId,
                extra_data: JSON.stringify({ ...extraData }),
            }),
            headers: this.headers,
            ...(this.signal && { signal: this.signal }),
        };

        return fetch(this.#fetchUrl('lead/add-extra-data'), fetchOptions);
    }

    /**
     * add player id
     * @param playerID
     * @returns {Promise<Response>}
     */
    createPlayer(playerID) {
        const fetchOptions = {
            method: 'POST',
            body: this.toUrlSearchParams({ playfab_id: playerID }),
            headers: this.headers,
            ...(this.signal && { signal: this.signal }),
        };

        return fetch(this.#fetchUrl('player'), fetchOptions);
    }

    /**
     * create player and sync with push lead
     * @param hash
     * @param campaign_id
     * @param playfab_id
     * @returns {Promise<Response>}
     */
    createPlayerAndSyncLead({ hash, campaign_id, playfab_id }) {
        const fetchOptions = {
            method: 'POST',
            body: this.toUrlSearchParams({
                campaign_id,
                playfab_id,
                hash,
            }),
            headers: this.headers,
            ...(this.signal && { signal: this.signal }),
        };

        return fetch(this.#fetchUrl('player/sync-lead'), fetchOptions);
    }
}

export default SharpDataStarService;
